import React, { FC, useCallback, useState } from "react";
import { ButtonProps, Dialog, Input } from "@xapp/components";

import { BasicIntegrationCard, IntegrationCardProp, IntegrationConnectionProps } from "./BasicIntegrationCard";

export interface LeadPerfectionConnectionProps extends IntegrationConnectionProps {
    integration: "leadperfection";
    clientId: string;
    srsId: string;
    sender?: string;
    defaultProductId?: string;
    rankId?: string;
    proId?: string;
}

export interface LeadPerfectionIntegrationCardProps extends IntegrationCardProp {
    clientId?: string;
    srsId?: string;
    sender?: string;
    defaultProductId?: string;
    rankId?: string;
    proId?: string;

    onConnect?(props: LeadPerfectionConnectionProps): void;
}

/**
 * LeadPerfection connection card.
 *
 * Deliberately does NOT offer a phone-type field. A capture form gives us a phone number, not a
 * line type, so any value chosen here would be invented — and a wrong type is worse than none,
 * since a contractor may drive texting or dialer behaviour off it. `phone1` is the primary number
 * and shows on the prospect's General tab regardless of type.
 *
 * There is also no job-type → productid mapping editor yet; the first pass ships a single default
 * product. `productIdMap` exists on the service and on the API's stored integration params, but it
 * is deliberately NOT on the `addLeadPerfectionIntegration` mutation — a map needs a JSON scalar or
 * a key/value list to be expressible in the schema. So building that editor means exposing the
 * param on the mutation first, then the UI; it is not a UI-only change.
 */
export const LeadPerfectionIntegrationCard: FC<LeadPerfectionIntegrationCardProps> = (props) => {
    const { checked, disabled, isLinked, isActive, onConnect, onDisconnect } = props;

    const [clientId, setClientId] = useState<string>(props.clientId || "");
    const [srsId, setSrsId] = useState<string>(props.srsId || "");
    const [sender, setSender] = useState<string>(props.sender || "");
    const [defaultProductId, setDefaultProductId] = useState<string>(props.defaultProductId || "");
    const [rankId, setRankId] = useState<string>(props.rankId || "");
    const [proId, setProId] = useState<string>(props.proId || "");
    const [showDialog, setShowDialog] = useState<boolean>(false);

    const resetForm = useCallback(() => {
        setClientId(props.clientId || "");
        setSrsId(props.srsId || "");
        setSender(props.sender || "");
        setDefaultProductId(props.defaultProductId || "");
        setRankId(props.rankId || "");
        setProId(props.proId || "");
    }, [props.clientId, props.srsId, props.sender, props.defaultProductId, props.rankId, props.proId]);

    const handleCloseDialog = useCallback(() => {
        resetForm();
        setShowDialog(false);
    }, [resetForm]);

    const handleLeadPerfectionClick = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>): void => {
            e.preventDefault();

            if (typeof checked === "boolean") {
                if (typeof props.onClick === "function") {
                    props.onClick(e);
                }
            } else {
                setShowDialog(true);
            }
        },
        [checked, props.onClick]
    );

    const handleConnectLeadPerfection = useCallback(() => {
        if (typeof onConnect === "function") {
            onConnect({
                integration: "leadperfection",
                clientId: clientId.trim(),
                srsId: srsId.trim(),
                sender: sender || undefined,
                defaultProductId: defaultProductId || undefined,
                rankId: rankId || undefined,
                proId: proId || undefined,
            });
            setShowDialog(false);
        }
    }, [onConnect, clientId, srsId, sender, defaultProductId, rankId, proId]);

    const actions: ButtonProps[] = [
        {
            label: "Close",
            variation: "secondary",
            onClick: handleCloseDialog,
        },
    ];

    if (typeof onConnect === "function") {
        actions.push({
            label: "Connect",
            variation: "primary",
            // clientId and srsId are the two LeadPerfection genuinely requires; without either,
            // every post is rejected or misfiled.
            disabled: !clientId.trim() || !srsId.trim(),
            onClick: handleConnectLeadPerfection,
        });
    }

    return (
        <>
            <BasicIntegrationCard
                checked={checked}
                disabled={disabled}
                imageUrl="/img/integrations/leadperfection/leadperfection-logo.svg"
                text="LeadPerfection"
                href={() => "/integrations/leadperfection"}
                onClick={handleLeadPerfectionClick}
                isLinked={isLinked}
                isActive={isActive}
                onDisconnect={onDisconnect}
            />
            <Dialog active={showDialog} actions={actions} title="LeadPerfection Setup" onClose={handleCloseDialog}>
                <p>
                    Both required values come from LeadPerfection, not from XAPP — ask your LeadPerfection
                    representative if you are not sure which to use. Your <strong>Client ID</strong> appears in your LP
                    Post URL, and the <strong>Source ID</strong> determines which source and sub-source new leads from
                    XAPP are attributed to.
                </p>
                <Input label="Client ID" value={clientId} onChange={(val) => setClientId(String(val))} />
                <Input label="Source ID (srs_id)" value={srsId} onChange={(val) => setSrsId(String(val))} />
                <p>
                    The rest are optional. <strong>Default Product ID</strong> must match an entry in your
                    LeadPerfection PRODUCTS table. <strong>Rank ID</strong> ships as 1, 2 (Plus) and 3 (Prime), but the
                    table is editable so confirm yours. <strong>Promoter Employee ID</strong> applies only to leads from
                    a home show or canvassing. <strong>Sender</strong> is the vendor name shown on the inbound lead and
                    defaults to &quot;XAPP AI&quot;.
                </p>
                <Input
                    label="Default Product ID (Optional)"
                    value={defaultProductId}
                    onChange={(val) => setDefaultProductId(String(val))}
                />
                <Input label="Rank ID (Optional)" value={rankId} onChange={(val) => setRankId(String(val))} />
                <Input
                    label="Promoter Employee ID (Optional)"
                    value={proId}
                    onChange={(val) => setProId(String(val))}
                />
                <Input label="Sender (Optional)" value={sender} onChange={(val) => setSender(String(val))} />
                <p>
                    After connecting, send a test lead and confirm it appears on your <strong>Inbound Leads</strong>{" "}
                    page in LeadPerfection. Leads arrive there for review rather than going straight into your lead
                    database, and LeadPerfection reports success even when it ignores a value it does not recognise — so
                    a successful connection test confirms we can reach them, not that every field was accepted.
                </p>
            </Dialog>
        </>
    );
};
